const TOKEN_KEY = 'auth_token';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5117';

/** True when running against localhost (no Easy Auth available). */
const isLocalDev = API_BASE_URL.includes('localhost') || API_BASE_URL.includes('127.0.0.1');

/**
 * Capture token from URL fragment if present (post-login redirect).
 * Call this early in app initialization.
 */
export function captureTokenFromUrl(): boolean {
  const hash = window.location.hash;
  if (!hash) return false;

  const params = new URLSearchParams(hash.substring(1));
  const token = params.get('token');
  if (token) {
    sessionStorage.setItem(TOKEN_KEY, token);
    // Clean URL fragment without triggering navigation
    history.replaceState(null, '', window.location.pathname + window.location.search);
    return true;
  }
  return false;
}

/** Get stored token or null. */
export function getAuthToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

/** Whether we have a token stored (or running locally where no token is needed). */
export function isAuthenticated(): boolean {
  if (isLocalDev) return true;
  return !!getAuthToken();
}

/** Clear stored token. */
export function clearAuthToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

/**
 * Redirect to Easy Auth login on the API domain.
 * After login, the /auth/redirect endpoint extracts the token and bounces back with it in the URL fragment.
 * No-op in local dev (backend bypasses auth for localhost).
 */
export function redirectToLogin(): void {
  if (isLocalDev) return;
  const returnUrl = encodeURIComponent(window.location.href);
  // Use API endpoint that extracts token from Easy Auth headers and redirects with it
  const redirectEndpoint = `${API_BASE_URL}/auth/redirect?returnUrl=${returnUrl}`;
  const encodedRedirectEndpoint = encodeURIComponent(redirectEndpoint);
  window.location.href = `${API_BASE_URL}/.auth/login/aad?post_login_redirect_uri=${encodedRedirectEndpoint}`;
}

/**
 * Logout: clear local token, then redirect to Easy Auth logout on API domain.
 */
export function logout(): void {
  clearAuthToken();
  if (isLocalDev) {
    window.location.href = '/';
    return;
  }
  const webAppUrl = window.location.origin;
  window.location.href = `${API_BASE_URL}/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(webAppUrl)}`;
}
